/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import StatusBadge from "@/shared/ui/components/basic/StatusBadge";
import type { StatusBadgeTone } from "@/shared/ui/components/basic/StatusBadge";
import { TrashIcon, UnstyledButton } from '@/shared/ui';
import { maskPhone } from "../utils/masks";
import type { ClientReferral } from "@services/requests/housien-services/ClientInsightsService";

interface ReferralRowProps {
  referral: ClientReferral;
  convertLabel: string;
  statusLabel: string;
  removeLabel: string;
  onConvert: () => void;
  onOpenConverted: () => void;
  onRemove: () => void;
}

/**
 * Maps a referral status to a status-badge tone.
 *
 * @param status - The referral status (converted/pending/other).
 * @returns The matching badge tone.
 */
function statusTone(status: string): StatusBadgeTone {
  if (status === "converted") return "success";
  if (status === "pending") return "warning";
  return "neutral";
}

/**
 * Derives up-to-two-character initials from a person's name.
 *
 * @param name - The full name.
 * @returns The uppercased initials, or a fallback marker.
 */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase() || "?";
}

/**
 * One indication row: convert/open converted clients; delete when not converted.
 *
 * @param props - The referral, action labels, and action callbacks.
 * @returns The rendered referral row.
 */
export function ClientReferralRow({
  referral,
  convertLabel,
  statusLabel,
  removeLabel,
  onConvert,
  onOpenConverted,
  onRemove,
}: ReferralRowProps) {
  const phone = maskPhone(referral.referred_phone);
  const isConverted = referral.status === "converted";

  return (
    <div className="flex items-center gap-[var(--space-3)] min-w-0">
      <div className="w-10 h-10 rounded-full bg-[var(--seed-base)] text-[var(--text-inverse)] flex items-center justify-center text-[length:var(--text-meta)] [font-weight:var(--font-bold)] shrink-0">
        {initials(referral.referred_name)}
      </div>
      <div className="min-w-0 flex-1">
        {isConverted ? (
          <UnstyledButton
            type="button"
            onClick={onOpenConverted}
            className="text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] text-[var(--seed-base)] hover:underline truncate bg-transparent border-none cursor-pointer p-0 text-left max-w-full"
          >
            {referral.referred_name}
          </UnstyledButton>
        ) : (
          <div className="text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] text-[var(--text-primary)] truncate">
            {referral.referred_name}
          </div>
        )}
        <div className="text-[length:var(--text-micro)] text-[var(--text-tertiary)] truncate">
          {phone || "—"}
        </div>
      </div>
      <div className="flex flex-col items-end gap-[var(--space-1)] shrink-0">
        <StatusBadge tone={statusTone(referral.status)}>{statusLabel}</StatusBadge>
        {!isConverted && (
          <div className="flex items-center gap-[var(--space-2)]">
            <UnstyledButton
              type="button"
              onClick={onConvert}
              className="text-[length:var(--text-meta)] text-[var(--seed-base)] hover:underline bg-transparent border-none cursor-pointer p-0"
            >
              {convertLabel}
            </UnstyledButton>
            <UnstyledButton
              type="button"
              onClick={onRemove}
              title={removeLabel}
              aria-label={removeLabel}
              className="text-[var(--text-tertiary)] hover:text-[var(--status-error)] bg-transparent border-none cursor-pointer p-0 inline-flex"
            >
              <TrashIcon width={14} height={14} />
            </UnstyledButton>
          </div>
        )}
      </div>
    </div>
  );
}
